/**
 * Google's, Fireflies', Fathom's, Notion's, Airtable's and 6sense's OAuth redirects land on
 * /integrations/{google,fireflies,fathom,notion,airtable,sixsense-mcp}/callback carrying a one-time `code` (plus
 * `state`, and Google's `scope`, `authuser`). Capture them and scrub the
 * address bar before Datadog, Clarity, PostHog or the router can record the
 * URL — the same reason slack-callback-url.ts exists. Held in memory only:
 * the callback page posts them to the backend straight away.
 */
export type WorkspaceOAuthCallback = {
  code?: string;
  state?: string;
  error?: string;
};

export const GOOGLE_CALLBACK_PATH = '/integrations/google/callback';
export const FIREFLIES_CALLBACK_PATH = '/integrations/fireflies/callback';
export const FATHOM_CALLBACK_PATH = '/integrations/fathom/callback';
export const NOTION_CALLBACK_PATH = '/integrations/notion/callback';
export const GONG_CALLBACK_PATH = '/integrations/gong/callback';
export const AIRTABLE_CALLBACK_PATH = '/integrations/airtable/callback';
export const SIXSENSE_MCP_CALLBACK_PATH = '/integrations/sixsense-mcp/callback';
const CALLBACK_PATHS = [
  GOOGLE_CALLBACK_PATH,
  FIREFLIES_CALLBACK_PATH,
  FATHOM_CALLBACK_PATH,
  NOTION_CALLBACK_PATH,
  GONG_CALLBACK_PATH,
  AIRTABLE_CALLBACK_PATH,
  SIXSENSE_MCP_CALLBACK_PATH,
];

let captured: WorkspaceOAuthCallback | null = null;

export function captureWorkspaceOAuthCallback(): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (!CALLBACK_PATHS.includes(path)) return;
  const params = new URL(window.location.href).searchParams;
  captured = {
    code: params.get('code') ?? undefined,
    state: params.get('state') ?? undefined,
    error: params.get('error') ?? undefined,
  };
  window.history.replaceState(window.history.state, '', path);
}

captureWorkspaceOAuthCallback();

/** What Google sent, or null once cleared (e.g. after a refresh). */
export function readWorkspaceOAuthCallback(): WorkspaceOAuthCallback | null {
  return captured;
}

export function clearWorkspaceOAuthCallback(): void {
  captured = null;
}

/**
 * Google Sheets and Google Docs share one OAuth client and one callback page,
 * so the card remembers which product the user is connecting before leaving
 * for Google, and the callback page reads it back. Same tab, so
 * sessionStorage survives the round trip; the backend still checks the
 * product against the signed state.
 */
const CONNECTING_KEY = 'cleanlist:google-connecting';

export function rememberConnectingProvider(provider: string): void {
  try {
    window.sessionStorage.setItem(CONNECTING_KEY, provider);
  } catch {
    // Storage unavailable: the callback page falls back to Google Sheets.
  }
}

export function takeConnectingProvider(): string | null {
  try {
    const provider = window.sessionStorage.getItem(CONNECTING_KEY);
    window.sessionStorage.removeItem(CONNECTING_KEY);
    return provider;
  } catch {
    return null;
  }
}
